import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext({
  items: [],
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
  updateQuantity: () => {},
});

// Use a versioned storage key so old test data doesn't persist forever
const STORAGE_KEY = 'gfn_cart_v2';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore storage errors
    }
  }, [items]);

  const addToCart = (product, quantity = 1) => {
    if (!product?._id) return;

    setItems((prev) => {
      const existing = prev.find((item) => item.productId === product._id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      const image =
        Array.isArray(product.images) && product.images.length > 0
          ? product.images[0]
          : null;

      const newItem = {
        productId: product._id,
        name: product.name,
        price: product.price,
        unit: product.unit,
        image,
        quantity,
        stockQuantity: product.stockQuantity,
      };

      return [...prev, newItem];
    });
  };

  const removeFromCart = (productId) => {
    setItems((prev) => prev.filter((item) => item.productId !== productId));
  };

  const clearCart = () => {
    setItems([]);
  };

  const updateQuantity = (productId, quantity) => {
    setItems((prev) =>
      prev
        .map((item) => {
          if (item.productId !== productId) return item;
          const maxQty = item.stockQuantity || item.quantity || 1;
          const nextQty = Math.max(1, Math.min(quantity, maxQty));
          return { ...item, quantity: nextQty };
        })
    );
  };

  const value = useMemo(
    () => ({
      items,
      addToCart,
      removeFromCart,
      clearCart,
      updateQuantity,
    }),
    [items]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);


